import { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material'
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Upload as UploadIcon,
  Block as DisableIcon,
  CheckCircle as EnableIcon,
} from '@mui/icons-material'
import Table, { Column } from '../../components/ui/Table'
import StatusChip from '../../components/ui/StatusChip'
import { useSKUs, useUpdateSKUStatus } from '../../hooks/api/useSKUs'
import { SKU } from '../../types'
import { format } from 'date-fns'
import SKUForm from '../../components/forms/SKUForm'
import BulkImportDialog from '../../components/forms/BulkImportDialog'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { getCompanyUom } from '../../utils/companyUom'
import { formatCurrencyWithLocale } from '../../utils/formatters'

const SKUListPage = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const uom = getCompanyUom(user?.company_uom)
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [search, setSearch] = useState('')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedSKU, setSelectedSKU] = useState<SKU | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create')
  const [importOpen, setImportOpen] = useState(false)

  const { data: skusData, isLoading } = useSKUs({
    page: page + 1,
    limit: rowsPerPage,
    search,
  })

  const updateSKUStatusMutation = useUpdateSKUStatus()

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value)
    setPage(0)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, sku: SKU) => {
    setAnchorEl(event.currentTarget)
    setSelectedSKU(sku)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedSKU(null)
  }

  const handleCreateSKU = () => {
    setFormMode('create')
    setSelectedSKU(null)
    setFormOpen(true)
  }

  const handleEditSKU = () => {
    setFormMode('edit')
    setFormOpen(true)
    setAnchorEl(null)
  }

  const handleToggleStatus = () => {
    if (selectedSKU) {
      updateSKUStatusMutation.mutate({
        id: selectedSKU._id,
        isActive: selectedSKU.is_active === false,
      })
    }
    setAnchorEl(null)
    setSelectedSKU(null)
  }

  const handleFormClose = () => {
    setFormOpen(false)
    setSelectedSKU(null)
  }

  const columns: Column<SKU>[] = [
    {
      id: 'sortOrder',
      label: 'Position',
      minWidth: 70,
      align: 'center',
      render: (value) => value?.toString() || '-',
    },
    {
      id: 'sku_code',
      label: 'SKU Code',
      minWidth: 90,
      align: 'center',
      render: (value) => value?.toString() || '-',
    },
    {
      id: 'name',
      label: 'SKU Name',
      minWidth: 180,
    },
    {
      id: 'packing',
      label: `Packing / ${uom.code}`,
      minWidth: 100,
      align: 'right',
      render: (value) => value?.toString() || '0',
    },
    {
      id: 'standard_rate',
      label: `Standard Rate / ${uom.code}`,
      minWidth: 120,
      align: 'right',
      render: (value) => `Rs ${value?.toFixed(2) || '0.00'}`,
    },
    {
      id: 'rate_per_pcs',
      label: 'Rate / PCS',
      minWidth: 110,
      align: 'right',
      render: (value) => value == null ? '—' : `Rs ${formatCurrencyWithLocale(value)}`,
    },
    {
      id: 'special_rate',
      label: `Special Rate / ${uom.code}`,
      minWidth: 120,
      align: 'right',
      render: (value) => `Rs ${value?.toFixed(2) || '0.00'}`,
    },
    {
      id: 'is_active',
      label: 'Status',
      minWidth: 100,
      align: 'center',
      render: (value) => (
        <StatusChip
          label={value === false ? 'Disabled' : 'Active'}
          variant={value === false ? 'neutral' : 'success'}
        />
      ),
    },
    {
      id: 'updatedAt',
      label: 'Last Updated',
      minWidth: 180,
      render: (value) => format(new Date(value), 'MMM dd, yyyy hh:mm a'),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 100,
      align: 'center',
      render: (_, row) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, row)}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ]

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">SKUs</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setImportOpen(true)}
          >
            Import CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateSKU}
          >
            Create SKU
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by SKU name or code..."
              value={search}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Table
        columns={columns}
        onRowClick={(sku) => navigate(`/skus/${sku._id}`)}
        data={skusData?.results?.map(sku => ({ ...sku, id: sku._id })) || []}
        loading={isLoading}
        pagination={skusData ? {
          page,
          rowsPerPage,
          total: skusData.info.total,
          onPageChange: setPage,
          onRowsPerPageChange: setRowsPerPage,
        } : undefined}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditSKU}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={handleToggleStatus}
          sx={{ color: selectedSKU?.is_active === false ? 'success.main' : 'warning.main' }}
        >
          {selectedSKU?.is_active === false ? (
            <EnableIcon sx={{ mr: 1 }} />
          ) : (
            <DisableIcon sx={{ mr: 1 }} />
          )}
          {selectedSKU?.is_active === false ? 'Enable' : 'Disable'}
        </MenuItem>
      </Menu>

      <SKUForm
        open={formOpen}
        onClose={handleFormClose}
        sku={selectedSKU}
        mode={formMode}
        totalSKUs={skusData?.info?.total || 0}
      />

      <BulkImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        entity="skus"
      />

    </Box>
  )
}

export default SKUListPage
